import type { ReactNode } from 'react';
import { cn } from './cn';

/**
 * Section header for a group of fields — a tinted icon chip, the label, then a hairline running to
 * the card edge. The rule IS the separator, so a section needs no `border-t` and 24px of padding
 * above it as well.
 *
 * This is the TOP of a three-level hierarchy, and the levels have to look different or a reader
 * cannot tell them apart (asked directly: "currently it is hard to decide which one is
 * header/label"):
 *
 *   SECTION   uppercase, coloured icon chip, a rule across the card   <- this component
 *   label     small, sentence case, subtle, no rule                   <- OField / InlineEditField
 *   **Value** 14px semibold, full contrast                            <- the thing being read
 *
 * So `type-overline` (uppercase) belongs to sections ONLY. A field label that is also uppercase
 * competes with its own section heading, which is exactly how this got confusing.
 *
 * Lifted out of RetailerDetailPage so the farmer dialog and the dealer page cannot drift.
 */
export function SectionHead({ icon, tint, children, hint, className }: {
  icon: ReactNode;
  /** Background + text classes for the icon chip, e.g. `bg-brand-600/10 text-brand-700`. */
  tint: string;
  children: ReactNode;
  /** Optional trailing note, e.g. a count or a state. Sits before the rule, quietly. */
  hint?: ReactNode;
  className?: string;
}) {
  return (
    <h4 className={cn('mb-2.5 flex items-center gap-2', className)}>
      <span className={cn('flex size-6 shrink-0 items-center justify-center rounded-md', tint)}>{icon}</span>
      <span className="type-overline text-content">{children}</span>
      {hint && <span className="type-caption text-subtle">{hint}</span>}
      <span className="ml-1 h-px flex-1 bg-line/70" aria-hidden />
    </h4>
  );
}

/**
 * The tints the field sections use, so two pages describing the same kind of thing pick the same
 * colour. Keyed by MEANING, not by page — "where something is" is sky wherever it appears.
 *
 * Red is deliberately absent: red means "a problem" app-wide, and a section heading is not a state.
 */
export const SECTION_TINT = {
  identity: 'bg-brand-600/10 text-brand-700 dark:bg-brand-400/10 dark:text-brand-400',
  contact: 'bg-emerald-500/10 text-emerald-600 dark:bg-emerald-400/10 dark:text-emerald-400',
  place: 'bg-sky-500/10 text-sky-600 dark:bg-sky-400/10 dark:text-sky-400',
  farm: 'bg-lime-600/10 text-lime-700 dark:bg-lime-400/10 dark:text-lime-400',
  money: 'bg-amber-500/10 text-amber-600 dark:bg-amber-400/10 dark:text-amber-400',
  assignment: 'bg-violet-500/10 text-violet-600 dark:bg-violet-400/10 dark:text-violet-400',
  documents: 'bg-indigo-500/10 text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-400',
  system: 'bg-slate-500/10 text-slate-600 dark:bg-slate-400/10 dark:text-slate-400',
} as const;
